import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor

/**
 * Rendering parameters for a [DiceSkin]: the face brush + pip color, plus an optional [innerFire]
 * that fills the die surface (for the Ember materials). Resolved against the theme for [DiceSkin.DEFAULT].
 */
data class DiceSkinStyle(
    val faceBrush: Brush,
    val pipColor: Color,
    val innerFire: FirePalette? = null,
    val surfaceColors: List<Color> = listOf(Color.White, Color(0xFFEFE9DC)),
    val pipColor3D: Color = pipColor,
)

private fun diceMaterial(
    colors: List<Color>,
    pipColor: Color,
    innerFire: FirePalette? = null,
    vertical: Boolean = false,
    pipColor3D: Color = pipColor,
) = DiceSkinStyle(
    faceBrush = when {
        colors.size == 1 -> SolidColor(colors.first())
        vertical -> Brush.verticalGradient(colors)
        else -> Brush.linearGradient(colors)
    },
    pipColor = pipColor,
    innerFire = innerFire,
    surfaceColors = colors,
    pipColor3D = pipColor3D,
)

@Composable
fun DiceSkin.style(): DiceSkinStyle = when (this) {
    // Subtle top-lit gradient (bright white → warm off-white) so the plain die still reads as a
    // three-dimensional object rather than a flat rounded square.
    DiceSkin.DEFAULT -> diceMaterial(
        colors = listOf(Color.White, Color(0xFFEFE9DC)),
        pipColor = MaterialTheme.colorScheme.primary,
        vertical = true,
        pipColor3D = Color(0xFF362C42),
    )
    DiceSkin.SILVER -> diceMaterial(
        colors = listOf(Color(0xFFF5F6F8), Color(0xFFB9BEC6), Color(0xFFE3E6EA)),
        pipColor = Color(0xFF2B2F36),
    )
    DiceSkin.GOLD -> diceMaterial(
        colors = listOf(Color(0xFFFFE9A8), Color(0xFFD4AF37), Color(0xFFFFF3C4)),
        pipColor = Color(0xFF4A3608),
    )
    // Dark face so the on-surface flames pop; white pips stay readable over the fire.
    DiceSkin.EMBER -> diceMaterial(listOf(Color(0xFF1A0A06)), Color.White, innerFire = FirePalette.ORANGE)
    DiceSkin.BLUE_EMBER -> diceMaterial(listOf(Color(0xFF06091A)), Color.White, innerFire = FirePalette.BLUE)
    DiceSkin.USA -> diceMaterial(
        colors = listOf(
                Color(0xFFB22234),
                Color(0xFFFFFFFF),
                Color(0xFFF7F7F7),
                Color(0xFF3C3B6E),
        ),
        pipColor = Color(0xFF0A2148),
    )
    // Nursery pastels — baby blue melting into powder pink and butter yellow, with soft crayon-purple
    // pips. Deliberately reads as "training wheels": the reward for finishing the practice round.
    DiceSkin.TRAINING -> diceMaterial(
        colors = listOf(Color(0xFFBDE0FE), Color(0xFFFFD1E3), Color(0xFFFFF3B0)),
        pipColor = Color(0xFF6B5B95),
    )
    DiceSkin.LUCKY -> diceMaterial(
        colors = listOf(Color(0xFF7F5AF0), Color(0xFF2CB67D), Color(0xFFFF8906)),
        pipColor = Color.White,
    )
    DiceSkin.HARVEST -> diceMaterial(
        colors = listOf(Color(0xFF742D36), Color(0xFFD96B28), Color(0xFFFFD67A)),
        pipColor = Color(0xFFFFF2CC),
    )
    DiceSkin.HALLOWEEN -> diceMaterial(
        colors = listOf(Color(0xFF281638), Color(0xFF783AA0), Color(0xFF161321)),
        pipColor = Color(0xFFCBFA72),
    )
    DiceSkin.CREATOR -> diceMaterial(
        colors = listOf(Color(0xFF101820), Color(0xFFE9C46A), Color(0xFF2A9D8F)),
        pipColor = Color.White,
    )
}
